import { createRouter, createWebHistory } from 'vue-router'
import { notificationService } from '@/services/notificationService'
import api from '@/services/api'

// Authentication Pages
import Signin from '@/views/Auth/SigninPage.vue'
import Signup from '@/views/Auth/SignupPage.vue'
import ForgotPassword from '@/views/Auth/ForgotPassword.vue'
import ResetPassword from '@/views/Auth/ResetPassword.vue'
import SetupPassword from '@/views/Auth/SetupPassword.vue'
import TwoStepVerification from '@/views/Auth/TwoStepVerification.vue'

// Main Role-Adaptive Dashboard
import DashboardView from '@/views/Pages/DashboardView.vue'

// Common Pages based on user types
import TenderDetailsView from '@/views/Pages/TenderDetails/TenderDetailsView.vue'
// import TemplateManagement from '@/views/Pages/Templates/TemplateManagement.vue'

// Admin Pages
// import AdminUnassignedTender from '@/views/Pages/Admin/UnassignedTender.vue'
import AdminTenderList from '@/views/Pages/Admin/TenderListview.vue'

import AdminWatchlistTender from '@/views/Pages/Admin/WatchListTender.vue'
import AdminAssignedTender from '@/views/Pages/Admin/AssignedTender.vue'
import AdminInProgressTender from '@/views/Pages/Admin/InProgressTenderView.vue'
import AdminCriteriaReview from '@/views/Pages/Admin/CriteriaReviewTenderView.vue'
import AdminPriceReview from '@/views/Pages/Admin/PriceReviewTenderView.vue'
import AdminReadyToBid from '@/views/Pages/Admin/ReadyToBidTenderView.vue'
import AdminNotFitTender from '@/views/Pages/Admin/NotFitTenderView.vue'
import AdminNotBiddedTender from '@/views/Pages/Admin/NotBiddedTenderView.vue'
import AdminSubmittedTender from '@/views/Pages/Admin/SubmittedTenderView.vue'
import AdminUnderQueryTender from '@/views/Pages/Admin/UnderQueryTenderView.vue'
import AdminWonTender from '@/views/Pages/Admin/WonTender.vue'
import AdminLostTender from '@/views/Pages/Admin/LostTender.vue'
import AdminEngineerTender from '@/views/Pages/Admin/EngineerTender.vue'
import AdminCompletedTender from '@/views/Pages/Admin/CompletedTender.vue'
import AdminRejectedTender from '@/views/Pages/Admin/RejectedTender.vue'
import AdminMediaManagement from '@/views/Pages/Admin/MediaManagement.vue'
import AdminUserManagement from '@/views/Pages/Admin/UserManagement.vue'
import AdminFeedbackManagement from '@/views/Pages/Admin/FeedbackManagement.vue'
import AdminReportsManagement from '@/views/Pages/Reports/ReportsManagement.vue'
import AdminInstalledAssets from '@/views/Pages/Admin/InstalledAssetsView.vue'

import UnauthorizedPage from '@/views/Pages/UnauthorizedView.vue'
import UserProfilePage from '@/views/Pages/UserProfileView.vue'

//Agent Pages
import AgentAssignedTenders from '@/views/Pages/Agent/AssignedTenderView.vue'
import AgentInProgressTenders from '@/views/Pages/Agent/InProgressTenderView.vue'
import AgentPriceReview from '@/views/Pages/Agent/PriceReviewTenderView.vue'
import AgentCriteriaReview from '@/views/Pages/Agent/CriteriaReviewTenderView.vue'
import AgentReadyToBid from '@/views/Pages/Agent/ReadyToBidTenderView.vue'
import AgentNotFitTender from '@/views/Pages/Agent/NotFitTenderView.vue'
import AgentNotBiddedTender from '@/views/Pages/Agent/NotBiddedTenderView.vue'
import AgentSubmittedTender from '@/views/Pages/Agent/SubmittedTenderView.vue'
import AgentUnderQueryTender from '@/views/Pages/Agent/UnderQueryTenderView.vue'
import AgentWonTender from '@/views/Pages/Agent/WonTenderView.vue'
import AgentLostTender from '@/views/Pages/Agent/LostTenderView.vue'
import AgentCompletedTender from '@/views/Pages/Agent/CompletedTenderView.vue'

// Import store for authentication check
import { useAuthStore } from '@/stores/auth'
import type { UserType } from '@/utils/roleHandler'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { left: 0, top: 0 }
  },
  routes: [
    // ======================
    // AUTHENTICATION ROUTES
    // ======================
    {
      path: '/',
      name: 'Signin',
      component: Signin,
      meta: {
        title: 'Sign In',
        public: true,
        hideLayout: true,
      },
    },
    {
      path: '/signup',
      name: 'Signup',
      component: Signup,
      meta: {
        title: 'Sign Up',
        public: true,
        hideLayout: true,
      },
    },
    {
      path: '/forgot-password',
      name: 'ForgotPassword',
      component: ForgotPassword,
      meta: {
        title: 'Reset Password',
        public: true,
        hideLayout: true,
      },
    },
    {
      path: '/setup-password',
      name: 'SetupPassword',
      component: SetupPassword,
      meta: {
        title: 'Setup Password',
        public: true,
        hideLayout: true,
      },
    },
    {
      path: '/reset-password',
      name: 'ResetPassword',
      component: ResetPassword,
      meta: {
        title: 'Reset Password',
        public: true,
        hideLayout: true,
      },
    },
    {
      path: '/two-step-verification',
      name: 'TwoStepVerification',
      component: TwoStepVerification,
      meta: {
        title: 'Two Step Verification',
        public: true,
        hideLayout: true,
      },
    },

    // ======================
    // MAIN DASHBOARD (USER TYPE-ADAPTIVE)
    // ======================
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: DashboardView,
      meta: {
        title: 'Dashboard',
        requiresAuth: true,
        breadcrumb: 'Dashboard',
      },
    },
    {
      path: '/manage-account',
      name: 'Manage Account',
      component: UserProfilePage,
      meta: {
        title: 'Manage Account',
        requiresAuth: true,
        breadcrumb: 'Manage Account',
      },
    },

    // ======================
    // ADMIN-ONLY ROUTES
    // ======================
    // {
    //   path: '/unassigned-tenders',
    //   name: 'UnassignedTender',
    //   component: AdminUnassignedTender,
    //   meta: {
    //     title: 'Unassigned Tenders',
    //     requiresAuth: true,
    //     requiredUserTypes: ['admin'] as UserType[],
    //     breadcrumb: 'Unassigned Tenders',
    //   },
    // },
    {
      path: '/tenders',
      name: 'tendersList',
      component: AdminTenderList,
      meta: {
        title: 'Tenders List',
        requiresAuth: true,
        requiredUserTypes: ['admin'] as UserType[],
        breadcrumb: 'Tenders List',
      },
    },
    {
      path: '/watchlist-tenders',
      name: 'WatchlistTender',
      component: AdminWatchlistTender,
      meta: {
        title: 'Watchlist Tenders',
        requiresAuth: true,
        requiredUserTypes: ['admin'] as UserType[],
        breadcrumb: 'Watchlist Tenders',
      },
    },
    {
      path: '/assigned-tenders-admin',
      name: 'AssignedTender',
      component: AdminAssignedTender,
      meta: {
        title: 'Assigned Tenders',
        requiresAuth: true,
        requiredUserTypes: ['admin'] as UserType[],
        breadcrumb: 'Assigned Tenders',
      },
    },
    {
      path: '/in-progress-admin',
      name: 'InProgressTender',
      component: AdminInProgressTender,
      meta: {
        title: 'InProgress Tenders',
        requiresAuth: true,
        requiredUserTypes: ['admin'] as UserType[],
        breadcrumb: 'InProgress Tenders',
      },
    },
    {
      path: '/price-review-admin',
      name: 'PriceReviewTender',
      component: AdminPriceReview,
      meta: {
        title: 'Price Review Tenders',
        requiresAuth: true,
        requiredUserTypes: ['admin'] as UserType[],
        breadcrumb: 'Price Review Tenders',
      },
    },
    {
      path: '/criteria-review-admin',
      name: 'CriteriaReviewTender',
      component: AdminCriteriaReview,
      meta: {
        title: 'Criteria Review Tenders',
        requiresAuth: true,
        requiredUserTypes: ['admin'] as UserType[],
        breadcrumb: 'Criteria Review Tenders',
      },
    },
    {
      path: '/ready-to-bid-admin',
      name: 'ReadyToBidTender',
      component: AdminReadyToBid,
      meta: {
        title: 'Ready to Bid Tenders',
        requiresAuth: true,
        requiredUserTypes: ['admin'] as UserType[],
        breadcrumb: 'Ready to Bid Tenders',
      },
    },
    {
      path: '/not-fit-admin',
      name: 'NotFitTender',
      component: AdminNotFitTender,
      meta: {
        title: 'Not Fit Tenders',
        requiresAuth: true,
        requiredUserTypes: ['admin'] as UserType[],
        breadcrumb: 'Not Fit Tenders',
      },
    },
    {
      path: '/not-bidded-admin',
      name: 'NotBiddedTender',
      component: AdminNotBiddedTender,
      meta: {
        title: 'Not Bidded Tenders',
        requiresAuth: true,
        requiredUserTypes: ['admin'] as UserType[],
        breadcrumb: 'Not Bidded Tenders',
      },
    },
    {
      path: '/submitted-tender-admin',
      name: 'SubmittedTender',
      component: AdminSubmittedTender,
      meta: {
        title: 'Submitted Tenders',
        requiresAuth: true,
        requiredUserTypes: ['admin'] as UserType[],
        breadcrumb: 'Submitted Tenders',
      },
    },
    {
      path: '/under-query-admin',
      name: 'UnderQueryTender',
      component: AdminUnderQueryTender,
      meta: {
        title: 'Under Query Tenders',
        requiresAuth: true,
        requiredUserTypes: ['admin'] as UserType[],
        breadcrumb: 'Under Query Tenders',
      },
    },
    {
      path: '/won-tenders',
      name: 'WonTender',
      component: AdminWonTender,
      meta: {
        title: 'Won Tenders',
        requiresAuth: true,
        requiredUserTypes: ['admin'] as UserType[],
        breadcrumb: 'Won Tenders',
      },
    },
    {
      path: '/lost-tenders',
      name: 'LostTender',
      component: AdminLostTender,
      meta: {
        title: 'Lost Tenders',
        requiresAuth: true,
        requiredUserTypes: ['admin'] as UserType[],
        breadcrumb: 'Lost Tenders',
      },
    },
    {
      path: '/engineer-tenders',
      name: 'EngineerTender',
      component: AdminEngineerTender,
      meta: {
        title: 'Engineer Tenders',
        requiresAuth: true,
        requiredUserTypes: ['admin'] as UserType[],
        breadcrumb: 'Engineer Tenders',
      },
    },
    {
      path: '/completed-tenders',
      name: 'CompletedTender',
      component: AdminCompletedTender,
      meta: {
        title: 'Completed Tenders',
        requiresAuth: true,
        requiredUserTypes: ['admin'] as UserType[],
        breadcrumb: 'Completed Tenders',
      },
    },
    {
      path: '/rejected-tenders',
      name: 'RejectedTender',
      component: AdminRejectedTender,
      meta: {
        title: 'Rejected Tenders',
        requiresAuth: true,
        requiredUserTypes: ['admin'] as UserType[],
        breadcrumb: 'Rejected Tenders',
      },
    },
    {
      path: '/media-management',
      name: 'MediaManagement',
      component: AdminMediaManagement,
      meta: {
        title: 'Media Management',
        requiresAuth: true,
        requiredUserTypes: ['admin'] as UserType[],
        breadcrumb: 'Media Management',
      },
    },
    {
      path: '/manage-users',
      name: 'UserManagement',
      component: AdminUserManagement,
      meta: {
        title: 'User Management',
        requiresAuth: true,
        requiredUserTypes: ['admin'] as UserType[],
        breadcrumb: 'User Management',
      },
    },
    {
      path: '/manage-reports',
      name: 'ReportsManagement',
      component: AdminReportsManagement,
      meta: {
        title: 'Reports Management',
        requiresAuth: true,
        requiredUserTypes: ['admin', 'engineer'] as UserType[],
        breadcrumb: 'Reports Management',
      },
    },
    {
      path: '/admin/assets',
      name: 'InstalledAssets',
      component: AdminInstalledAssets,
      meta: {
        title: 'Installed Assets',
        requiresAuth: true,
        requiredUserTypes: ['admin'] as UserType[],
        breadcrumb: 'Installed Assets',
      },
    },
    {
      path: '/manage-feedback',
      name: 'FeedbackManagement',
      component: AdminFeedbackManagement,
      meta: {
        title: 'Client Feedback Management',
        requiresAuth: true,
        requiredUserTypes: ['admin'] as UserType[],
        breadcrumb: 'Feedback Management',
      },
    },
    // {
    //   path: '/template-management',
    //   name: 'Template Management',
    //   component: TemplateManagement,
    //   meta: {
    //     title: 'Template Management',
    //     requiresAuth: true,
    //     // requiredUserTypes: ['admin', 'agent'] as UserType[],
    //     breadcrumb: 'Template Management',
    //   },
    // },

    // ======================
    // COMMON ROUTES (All authenticated users)
    // ======================
    {
      path: '/tenders/:id',
      name: 'TenderDetails',
      component: TenderDetailsView,
      meta: {
        title: 'Tender Details',
        requiresAuth: true,
        breadcrumb: 'Tender Details',
      },
    },
    // ======================
    // AGENT ROUTES
    // ======================
    {
      path: '/assigned-tenders-agent',
      name: 'AssignedTendersAgent',
      component: AgentAssignedTenders,
      meta: {
        title: 'Assigned Tenders',
        requiresAuth: true,
        requiredUserTypes: ['agent'] as UserType[],
        breadcrumb: 'Assigned Tenders',
      },
    },
    {
      path: '/in-progress-agent',
      name: 'InProgressAgent',
      component: AgentInProgressTenders,
      meta: {
        title: 'In Progress',
        requiresAuth: true,
        requiredUserTypes: ['agent'] as UserType[],
        breadcrumb: 'In Progress',
      },
    },
    {
      path: '/price-review-agent',
      name: 'PriceReviewAgent',
      component: AgentPriceReview,
      meta: {
        title: 'Price Review',
        requiresAuth: true,
        requiredUserTypes: ['agent'] as UserType[],
        breadcrumb: 'Price Review',
      },
    },
    {
      path: '/criteria-review-agent',
      name: 'CriteriaReviewAgent',
      component: AgentCriteriaReview,
      meta: {
        title: 'Criteria Review',
        requiresAuth: true,
        requiredUserTypes: ['agent'] as UserType[],
        breadcrumb: 'Criteria Review',
      },
    },
    {
      path: '/ready-to-bid-agent',
      name: 'ReadyToBidAgent',
      component: AgentReadyToBid,
      meta: {
        title: 'Ready To Bid',
        requiresAuth: true,
        requiredUserTypes: ['agent'] as UserType[],
        breadcrumb: 'Ready To Bid',
      },
    },
    {
      path: '/not-fit-agent',
      name: 'NotFitAgent',
      component: AgentNotFitTender,
      meta: {
        title: 'Not Fit',
        requiresAuth: true,
        requiredUserTypes: ['agent'] as UserType[],
        breadcrumb: 'Not Fit',
      },
    },
    {
      path: '/not-bidded-agent',
      name: 'NotBiddedAgent',
      component: AgentNotBiddedTender,
      meta: {
        title: 'Not Bidded',
        requiresAuth: true,
        requiredUserTypes: ['agent'] as UserType[],
        breadcrumb: 'Not Bidded',
      },
    },
    {
      path: '/submitted-tender-agent',
      name: 'SubmittedTenderAgent',
      component: AgentSubmittedTender,
      meta: {
        title: 'Submitted Tender',
        requiresAuth: true,
        requiredUserTypes: ['agent'] as UserType[],
        breadcrumb: 'Submitted Tender',
      },
    },
    {
      path: '/under-query-agent',
      name: 'UnderQueryAgent',
      component: AgentUnderQueryTender,
      meta: {
        title: 'Under Query Tender',
        requiresAuth: true,
        requiredUserTypes: ['agent'] as UserType[],
        breadcrumb: 'Under Query Tender',
      },
    },
    {
      path: '/won-tender-agent',
      name: 'WonTenderAgent',
      component: AgentWonTender,
      meta: {
        title: 'Won Tender',
        requiresAuth: true,
        requiredUserTypes: ['agent'] as UserType[],
        breadcrumb: 'Won Tender',
      },
    },
    {
      path: '/lost-tender-agent',
      name: 'LostTenderAgent',
      component: AgentLostTender,
      meta: {
        title: 'Lost Tender',
        requiresAuth: true,
        requiredUserTypes: ['agent'] as UserType[],
        breadcrumb: 'Lost Tender',
      },
    },
    {
      path: '/completed-tender-agent',
      name: 'CompletedTenderAgent',
      component: AgentCompletedTender,
      meta: {
        title: 'Completed Tender',
        requiresAuth: true,
        requiredUserTypes: ['agent'] as UserType[],
        breadcrumb: 'Completed Tender',
      },
    },

    // ======================
    // ERROR/404 ROUTE
    // ======================
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      component: () => import('@/views/Pages/ErrorPageView.vue'),
      meta: {
        title: 'Page Not Found',
        public: true,
        hideLayout: true,
      },
    },
    {
      path: '/unauthorized',
      name: 'Unauthorized',
      component: UnauthorizedPage,
      meta: {
        title: 'Unauthorized',
        public: true,
        hideLayout: true,
      },
    },
  ],
})

// ======================
// ROUTE GUARDS
// ======================
router.beforeEach(async (to, from, next) => {
  // Set document title
  const pageTitle = to.meta.title || 'Tender Management System'
  document.title = `Tender Management System | ${pageTitle}`

  const authStore = useAuthStore()

  // Initialize auth store if needed
  if (!authStore.isInitialized && !to.meta.public) {
    authStore.initializeFromStorage()
  }

  // Check if route is public
  if (to.meta.public) {
    // If user is already authenticated and tries to access public route (like login),
    // redirect them to dashboard
    if (
      authStore.isAuthenticated &&
      (to.path === '/' || to.path === '/signin' || to.path === '/signup')
    ) {
      console.log('🔄 Already authenticated, redirecting to dashboard')
      return next('/dashboard')
    }
    return next()
  }

  // Check if route requires authentication
  if (to.meta.requiresAuth) {
    // Check if user is authenticated
    if (!authStore.isAuthenticated) {
      console.warn('🔒 Authentication required, redirecting to login')
      // Redirect to login if not authenticated
      return next({
        path: '/',
        query: { redirect: to.fullPath },
      })
    }

    // Check if user is active
    if (authStore.user?.status !== 'active') {
      console.warn('⚠️ User account is not active')
      authStore.clearAuth()
      return next({
        path: '/',
        query: { error: 'account_inactive' },
      })
    }

    if (to.name === 'TenderDetails' && to.params.id) {
      const authStore = useAuthStore()
      const userType = authStore.user?.userType
      const tenderId = to.params.id as string

      // If user is agent/engineer, check if they're assigned to this tender
      if (userType === 'agent' || userType === 'engineer') {
        try {
          const response = await api.get(`/api/tenders/${tenderId}/check-access`)

          if (!response.data.hasAccess) {
            console.warn(`⛔ Agent/Engineer tried to access unauthorized tender: ${tenderId}`)
            notificationService.warning('You are not authorized to view this tender.', 3000)
            return next('/Unauthorized')
          }
        } catch (error) {
          console.error('Error checking tender access:', error)
          notificationService.error('Failed to verify tender access.', 3000)
          return next('/Unauthorized')
        }
      }
    }

    // Check user type-based access
    const requiredUserTypes = to.meta.requiredUserTypes as UserType[] | undefined

    if (requiredUserTypes && requiredUserTypes.length > 0) {
      const userType = authStore.user?.userType as UserType | undefined

      if (!userType) {
        console.warn('⚠️ User type not found in user data')
        return next('/dashboard')
      }

      if (!requiredUserTypes.includes(userType)) {
        // User doesn't have required user type, redirect to dashboard
        console.warn(`⛔ Access denied: User type '${userType}' cannot access '${to.path}'`)

        // Use notification service instead of ToastService
        notificationService.warning(
          'You do not have permission to access this page.',
          3000
        )

        return next('/dashboard')
      }
    }

    // All checks passed, allow navigation
    console.log(`✅ Access granted to '${to.path}' for user type '${authStore.user?.userType}'`)
    return next()
  }

  // Default allow navigation
  next()
})

// Global navigation error handler
router.onError((error) => {
  console.error('🚨 Router error:', error)
})

export default router
